import { Injectable } from '@angular/core';
import { forkJoin, from, Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import {
  buildProductHistoriquePayload,
  createProductHistoriqueCorrelationId,
  normalizeProductHistoriqueSnapshot
} from './product-historique.util';
import { ProductsService } from 'app/services/products/products.service';
import { CategorieService } from 'app/services/categorie-unite-marque/categorie.service';
import { MarqueService } from 'app/services/categorie-unite-marque/marque.service';
import { UniteService } from 'app/services/categorie-unite-marque/unite.service';
import { Tva, TvaService } from 'app/main/parametre/taxe-tva/parametre-tva/tva.service';
import { Taxe, TaxeService } from 'app/main/parametre/taxe-tva/parametre-taxe/taxe.service';
import { Product } from 'app/models/Product';
import { Categorie } from 'app/models/categorie';
import { Marque } from 'app/models/marque';
import { Unite } from 'app/models/Unite';
import {
  applyDocumentImportProductApiShape,
  normalizeImportHeaderLabel,
  parseProductXlsxImportSheet,
  parseTvaImportValue,
  readImportCellTrimmed
} from 'app/main/pages/shared/document/document-product-xlsx-import.helper';

export type ProductXlsxImportMode = 'import' | 'reimport';

export interface ProductXlsxImportResult {
  importedCount: number;
  updatedCount: number;
  errors: string[];
}

interface ParsedImportRow {
  payload: Record<string, unknown>;
  line: number;
  isFamilyRow: boolean;
  refFabricant: string;
  codeBarre: string;
}

interface ReimportIndex {
  byCodeBarre: Map<string, Product>;
  byRefFabricant: Map<string, Product[]>;
}

@Injectable({ providedIn: 'root' })
export class ProductXlsxImportService {
  constructor(
    private productsService: ProductsService,
    private tvaService: TvaService,
    private taxeService: TaxeService,
    private categorieService: CategorieService,
    private marqueService: MarqueService,
    private uniteService: UniteService
  ) {}

  importFromFile(
    file: File,
    mode: ProductXlsxImportMode,
    stationId: number,
    clientId: number
  ): Observable<ProductXlsxImportResult> {
    return from(this.readFileAsArrayBuffer(file)).pipe(
      switchMap((buffer) => {
        const parsed = parseProductXlsxImportSheet(buffer);
        if ('error' in parsed) {
          return of({
            importedCount: 0,
            updatedCount: 0,
            errors: [String((parsed as { error: string }).error)]
          });
        }
        if (parsed.missingLabels.length > 0) {
          return of({
            importedCount: 0,
            updatedCount: 0,
            errors: [
              'Colonnes obligatoires manquantes : ' +
                parsed.missingLabels.join(', ') +
                '. Ex. attendu : nom, type, prix unitaire, ref_interne.'
            ]
          });
        }
        if (!parsed.dataRows.length) {
          return of({
            importedCount: 0,
            updatedCount: 0,
            errors: ['Aucune donnée trouvée dans le fichier']
          });
        }
        if (!stationId || !clientId) {
          return of({
            importedCount: 0,
            updatedCount: 0,
            errors: ['Utilisateur ou station non valide. Veuillez vous reconnecter.']
          });
        }

        const referentials$ = forkJoin({
          tvaListe: this.tvaService.getTvaList(stationId).pipe(
            map((r) => r?.tvaListe || []),
            catchError(() => of([] as Tva[]))
          ),
          taxes: this.taxeService.getTaxeList(stationId).pipe(
            map((r: { taxes?: Taxe[] }) => r?.taxes || []),
            catchError(() => of([] as Taxe[]))
          ),
          categories: this.categorieService.getCategoriePerStation(stationId).pipe(
            catchError(() => of([] as Categorie[]))
          ),
          marques: this.marqueService.getMarquePerStation(stationId).pipe(
            catchError(() => of([] as Marque[]))
          ),
          unites: this.uniteService.getUnitePerStation(stationId).pipe(
            catchError(() => of([] as Unite[]))
          ),
          existingProducts:
            mode === 'reimport'
              ? this.loadAllProductsForStation(stationId)
              : of([] as Product[])
        });

        return referentials$.pipe(
          switchMap((refs) =>
            from(
              this.buildImportRows(
                parsed.headerMap,
                parsed.dataRows,
                refs.tvaListe,
                refs.taxes,
                refs.categories,
                refs.marques,
                refs.unites,
                stationId,
                clientId
              )
            ).pipe(
              switchMap(({ rows, errors }) => {
                if (!rows.length) {
                  return of({ importedCount: 0, updatedCount: 0, errors });
                }
                if (mode === 'import') {
                  return this.runBulkImport(rows, errors, stationId, clientId);
                }
                const index = this.buildReimportIndex(refs.existingProducts);
                return this.runReimport(rows, errors, index, stationId, clientId, clientId);
              })
            )
          ),
          catchError(() =>
            of({
              importedCount: 0,
              updatedCount: 0,
              errors: ['Impossible de charger les référentiels ou de traiter le fichier.']
            })
          )
        );
      }),
      catchError(() =>
        of({
          importedCount: 0,
          updatedCount: 0,
          errors: ['Erreur lors de la lecture du fichier']
        })
      )
    );
  }

  private readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        const buffer = e.target?.result;
        if (!buffer) {
          reject(new Error('empty file'));
          return;
        }
        resolve(buffer as ArrayBuffer);
      };
      reader.onerror = () => reject(new Error('read error'));
      reader.readAsArrayBuffer(file);
    });
  }

  private async buildImportRows(
    headerMap: Record<string, number>,
    dataRows: unknown[][],
    tvaCache: Tva[],
    taxeCache: Taxe[],
    catCache: Categorie[],
    marqueCache: Marque[],
    uniteCache: Unite[],
    stationId: number,
    clientId: number
  ): Promise<{ rows: ParsedImportRow[]; errors: string[] }> {
    const errors: string[] = [];
    const rows: ParsedImportRow[] = [];
    const seenCodeBarres = new Set<string>();
    const seenRefInternes = new Set<string>();

    for (let i = 0; i < dataRows.length; i++) {
      const row = dataRows[i] as unknown[];
      const line = i + 2;

      const cellStr = (key: string): string | null => readImportCellTrimmed(row, headerMap, key);
      const cellNum = (key: string): number | null => {
        const s = cellStr(key);
        if (s == null || s === '') {
          return null;
        }
        const n = parseFloat(s.replace(',', '.'));
        return Number.isNaN(n) ? null : n;
      };

      const nom = row[headerMap.nom] != null ? String(row[headerMap.nom]).trim() : '';
      const type =
        row[headerMap.type] != null
          ? String(row[headerMap.type]).trim().toLowerCase()
          : 'matériel';
      const prix_unitaire =
        row[headerMap.prix] != null
          ? parseFloat(String(row[headerMap.prix]).replace(',', '.'))
          : 0;
      const ref_interne =
        row[headerMap.ref_interne] != null ? String(row[headerMap.ref_interne]).trim() : '';

      if (!nom) {
        errors.push(`Ligne ${line} : Le nom est obligatoire`);
        continue;
      }

      if (ref_interne && seenRefInternes.has(ref_interne)) {
        errors.push(`Ligne ${line} : réf. interne « ${ref_interne} » en double dans le fichier — ligne ignorée`);
        continue;
      }
      if (ref_interne) {
        seenRefInternes.add(ref_interne);
      }

      const codeBarreRaw = cellStr('code_barre') ?? '';
      if (codeBarreRaw) {
        const ck = codeBarreRaw.trim();
        if (seenCodeBarres.has(ck)) {
          errors.push(`Ligne ${line} : code-barres « ${ck} » en double dans le fichier — ligne ignorée`);
          continue;
        }
        seenCodeBarres.add(ck);
      }

      try {
        const product: Record<string, unknown> = {
          type,
          nom,
          station_id: stationId,
          client_id: clientId,
          ...(prix_unitaire > 0 && { prix_unitaires: prix_unitaire }),
          ...(ref_interne && { ref_interne }),
          ...(cellStr('ref_fabricant') && { ref_fabricant: cellStr('ref_fabricant') }),
          ...(cellStr('description') && { description: cellStr('description') }),
          ...(codeBarreRaw && { code_barre: codeBarreRaw }),
          ...(cellStr('couleur') && { couleur: cellStr('couleur') }),
          ...(cellStr('taille') && { taille: cellStr('taille') }),
          ...(cellStr('rayon') && { rayon: cellStr('rayon') }),
          ...(cellStr('famille') && { famille: cellStr('famille') }),
          ...(cellStr('sous_famille') && { sous_famille: cellStr('sous_famille') }),
          ...(cellNum('result_ttc') != null && { result_ttc: cellNum('result_ttc') }),
          ...(cellNum('result_ht') != null && { result_ht: cellNum('result_ht') })
        };

        const tvaCell = readImportCellTrimmed(row, headerMap, 'tva');
        if (tvaCell != null && tvaCell !== '') {
          const idTva = await this.rxFirst(
            this.resolveImportedProductTvaId(tvaCell, tvaCache, stationId, clientId)
          );
          const idNum = Number(idTva);
          if (!Number.isFinite(idNum) || idNum <= 0) {
            throw new Error(`TVA non résolue pour « ${tvaCell} ».`);
          }
          product.tva_id = idNum;
        }

        if (cellStr('taxe')) {
          product.taxe_id = await this.rxFirst(
            this.resolveOrCreateTaxeId(cellStr('taxe')!, taxeCache, stationId, clientId)
          );
        }
        if (cellStr('categorie')) {
          product.categories_id = await this.rxFirst(
            this.resolveOrCreateCategorieId(cellStr('categorie')!, catCache, stationId, clientId)
          );
        }
        if (cellStr('marque')) {
          product.marque_id = await this.rxFirst(
            this.resolveOrCreateMarqueId(cellStr('marque')!, marqueCache, stationId, clientId)
          );
        }
        if (cellStr('unite')) {
          product.unite_id = await this.rxFirst(
            this.resolveOrCreateUniteId(cellStr('unite')!, uniteCache, stationId, clientId)
          );
        }

        for (let p = 1; p <= 12; p++) {
          const pk = `param${p}`;
          const pv = cellStr(pk);
          if (pv) {
            product[pk] = pv;
          }
        }

        applyDocumentImportProductApiShape(product, clientId, stationId);

        const refFabricant = this.normalizeRefFabricant(
          cellStr('ref_fabricant') ?? (product.ref_fabricant as string | undefined)
        );
        const codeBarre = this.normalizeCodeBarre(codeBarreRaw);
        const isFamilyRow = this.isFamilyPrimaryStyleBarcode(codeBarreRaw || codeBarre);

        rows.push({
          payload: product,
          line,
          isFamilyRow,
          refFabricant,
          codeBarre
        });
      } catch (rowErr: unknown) {
        const msg = rowErr instanceof Error ? rowErr.message : String(rowErr);
        errors.push(`Ligne ${line} : ${msg}`);
      }
    }

    return { rows, errors };
  }

  private runBulkImport(
    rows: ParsedImportRow[],
    initialErrors: string[],
    stationId: number,
    userId: number
  ): Observable<ProductXlsxImportResult> {
    const payload = rows.map((r) => r.payload);
    return this.productsService.bulkAddProducts(payload).pipe(
      map(() => ({
        importedCount: rows.length,
        updatedCount: 0,
        errors: initialErrors
      })),
      catchError((error) => {
        if (this.isLikelyUniquenessConflict(error)) {
          return this.runSequentialAdd(rows, initialErrors, stationId, userId);
        }
        return of({
          importedCount: 0,
          updatedCount: 0,
          errors: [...initialErrors, this.extractBulkImportErrorMessage(error)]
        });
      })
    );
  }

  private runSequentialAdd(
    rows: ParsedImportRow[],
    initialErrors: string[],
    stationId: number,
    userId: number
  ): Observable<ProductXlsxImportResult> {
    return new Observable((observer) => {
      const errors = [...initialErrors];
      let importedCount = 0;
      let idx = 0;
      const batchId = createProductHistoriqueCorrelationId();

      const step = () => {
        if (idx >= rows.length) {
          observer.next({ importedCount, updatedCount: 0, errors });
          observer.complete();
          return;
        }
        const row = rows[idx++];
        this.productsService.addProduct(stationId, row.payload).subscribe({
          next: (response) => {
            importedCount++;
            this.logImportHistorique({
              action: 'IMPORT',
              stationId,
              userId,
              batchId,
              response,
              snapshotApres: row.payload,
              commentaire: `Import Excel ligne ${row.line}`
            });
            step();
          },
          error: (err) => {
            errors.push(`Ligne ${row.line} : ${this.extractBulkImportErrorMessage(err)}`);
            step();
          }
        });
      };

      step();
    });
  }

  private runReimport(
    rows: ParsedImportRow[],
    initialErrors: string[],
    index: ReimportIndex,
    stationId: number,
    clientId: number,
    userId: number
  ): Observable<ProductXlsxImportResult> {
    return new Observable((observer) => {
      const errors = [...initialErrors];
      let importedCount = 0;
      let updatedCount = 0;
      let idx = 0;
      const ctx = { station_id: stationId, client_id: clientId };
      const batchId = createProductHistoriqueCorrelationId();

      const step = () => {
        if (idx >= rows.length) {
          observer.next({ importedCount, updatedCount, errors });
          observer.complete();
          return;
        }

        const row = rows[idx++];
        const targets = this.findReimportTargets(row, index);

        if (!targets.length) {
          this.productsService.addProduct(stationId, row.payload).subscribe({
            next: (response) => {
              importedCount++;
              this.logImportHistorique({
                action: 'IMPORT',
                stationId,
                userId,
                batchId,
                response,
                snapshotApres: row.payload,
                commentaire: `Réimport Excel ligne ${row.line} (création)`
              });
              step();
            },
            error: (err) => {
              errors.push(`Ligne ${row.line} : ${this.extractBulkImportErrorMessage(err)}`);
              step();
            }
          });
          return;
        }

        const updates = targets.map((existing) => {
          const body =
            row.isFamilyRow && targets.length > 1
              ? this.mergeFamilyReimportPayload(row.payload, existing)
              : this.mergeVariantReimportPayload(row.payload, existing);
          return this.productsService.updateProduct(existing.id as number, body, ctx).pipe(
            map((response) => ({
              response,
              existing,
              body
            }))
          );
        });

        forkJoin(updates).subscribe({
          next: (results) => {
            updatedCount += results.length;
            for (const result of results) {
              this.logImportHistorique({
                action: 'REIMPORT',
                stationId,
                userId,
                batchId,
                response: result.response,
                snapshotAvant: this.productToHistoriqueSnapshot(result.existing),
                snapshotApres: result.body,
                commentaire: `Réimport Excel ligne ${row.line} (mise à jour)`
              });
            }
            step();
          },
          error: (err) => {
            errors.push(`Ligne ${row.line} : ${this.extractBulkImportErrorMessage(err)}`);
            step();
          }
        });
      };

      step();
    });
  }

  private productToHistoriqueSnapshot(product: Product): Record<string, unknown> {
    return normalizeProductHistoriqueSnapshot(product as unknown as Record<string, unknown>);
  }

  private logImportHistorique(opts: {
    action: 'IMPORT' | 'REIMPORT';
    stationId: number;
    userId: number;
    batchId: string;
    response: unknown;
    snapshotApres: Record<string, unknown>;
    snapshotAvant?: Record<string, unknown> | null;
    commentaire?: string | null;
  }): void {
    const product = (opts.response as { product?: Record<string, unknown> })?.product;
    const productId = product?.['id'] != null ? Number(product['id']) : NaN;
    if (!Number.isFinite(productId) || productId <= 0 || opts.stationId <= 0 || opts.userId <= 0) {
      return;
    }

    const snapshotApres = normalizeProductHistoriqueSnapshot(
      (product as Record<string, unknown>) ?? opts.snapshotApres
    );
    const payload = buildProductHistoriquePayload({
      stationId: opts.stationId,
      userId: opts.userId,
      productId,
      action: opts.action,
      source: 'product-xlsx-import',
      snapshotAvant: opts.snapshotAvant ?? null,
      snapshotApres,
      batchId: opts.batchId,
      commentaire: opts.commentaire ?? null
    });

    this.productsService.postProductHistorique(payload).pipe(catchError(() => of(null))).subscribe();
  }

  private findReimportTargets(row: ParsedImportRow, index: ReimportIndex): Product[] {
    if (!row.isFamilyRow && row.codeBarre) {
      const found = index.byCodeBarre.get(row.codeBarre);
      return found ? [found] : [];
    }
    if (row.refFabricant) {
      const key = this.refFabricantKey(row.refFabricant);
      return index.byRefFabricant.get(key) ?? [];
    }
    return [];
  }

  private buildReimportIndex(products: Product[]): ReimportIndex {
    const byCodeBarre = new Map<string, Product>();
    const byRefFabricant = new Map<string, Product[]>();

    for (const p of products) {
      if (this.isProductArchived(p) || p.id == null) {
        continue;
      }
      const ref = this.extractRefFabricant(p);
      const cb = this.normalizeCodeBarre(p.code_barre);
      if (cb && !this.isFamilyPrimaryStyleBarcode(cb)) {
        byCodeBarre.set(cb, p);
      }
      if (ref) {
        const key = this.refFabricantKey(ref);
        const list = byRefFabricant.get(key) ?? [];
        list.push(p);
        byRefFabricant.set(key, list);
      }
    }

    return { byCodeBarre, byRefFabricant };
  }

  private loadAllProductsForStation(stationId: number): Observable<Product[]> {
    const limit = 500;
    const load = (page: number, acc: Product[]): Observable<Product[]> =>
      this.productsService.getProductsByStationId(stationId, page, limit, false).pipe(
        switchMap((raw: unknown) => {
          const r = raw as {
            data?: Product[];
            products?: Product[];
            meta?: { total?: number; pages?: number; nb_total_pages?: number };
          };
          const pageRows = Array.isArray(r?.data) ? r.data : Array.isArray(r?.products) ? r.products : [];
          const merged = [...acc, ...pageRows];
          const metaTotal = r.meta?.total;
          const totalPagesMeta = r.meta?.pages ?? r.meta?.nb_total_pages;

          if (pageRows.length === 0) {
            return of(merged);
          }
          if (totalPagesMeta != null && page >= totalPagesMeta) {
            return of(merged);
          }
          if (metaTotal != null && merged.length >= metaTotal) {
            return of(merged);
          }
          if (pageRows.length < limit) {
            return of(merged);
          }
          return load(page + 1, merged);
        })
      );

    return load(1, []);
  }

  private mergeFamilyReimportPayload(
    base: Record<string, unknown>,
    variant: Product
  ): Record<string, unknown> {
    return {
      ...base,
      ref_interne: variant.ref_interne ?? base.ref_interne,
      code_barre: variant.code_barre ?? base.code_barre,
      couleur: variant.couleur ?? base.couleur,
      taille: variant.taille ?? base.taille
    };
  }

  private mergeVariantReimportPayload(
    base: Record<string, unknown>,
    existing: Product
  ): Record<string, unknown> {
    return {
      ...base,
      ref_interne: base.ref_interne ?? existing.ref_interne,
      code_barre: base.code_barre ?? existing.code_barre,
      couleur: base.couleur ?? existing.couleur,
      taille: base.taille ?? existing.taille
    };
  }

  private isFamilyPrimaryStyleBarcode(code_barre: unknown): boolean {
    if (code_barre == null || code_barre === '') {
      return true;
    }
    const s = String(code_barre).trim();
    if (s === '' || s === '0') {
      return true;
    }
    const n = Number(s.replace(',', '.'));
    return !Number.isNaN(n) && n === 0;
  }

  private normalizeCodeBarre(raw: unknown): string {
    if (raw == null || raw === '') {
      return '';
    }
    return String(raw).trim();
  }

  private normalizeRefFabricant(raw: unknown): string {
    if (raw == null) {
      return '';
    }
    return String(raw).trim();
  }

  private extractRefFabricant(p: Product): string {
    const row = p as Product & {
      ref_fabriquant?: string;
      refFabriquant?: string;
      reference_fabricant?: string;
    };
    return this.normalizeRefFabricant(
      row.ref_fabricant ?? row.ref_fabriquant ?? row.refFabriquant ?? row.reference_fabricant
    );
  }

  private refFabricantKey(ref: string): string {
    return ref.trim().toLocaleLowerCase('fr');
  }

  private isProductArchived(p: Product): boolean {
    const row = p as Product & { is_archive?: unknown; isArchive?: unknown };
    const v = row.is_archive ?? row.isArchive;
    return v === true || v === 1 || v === '1';
  }

  private rxFirst<T>(source: Observable<T>): Promise<T> {
    return source.pipe(take(1)).toPromise() as Promise<T>;
  }

  private importLabelEquals(a: string, b: string): boolean {
    return normalizeImportHeaderLabel(a) === normalizeImportHeaderLabel(b);
  }

  private resolveImportedProductTvaId(
    raw: string,
    cache: Tva[],
    stationId: number,
    clientId: number
  ): Observable<number> {
    const trimmed = raw.trim();
    const parsedFile = parseTvaImportValue(trimmed);
    const lookupFromByValeur$ =
      parsedFile !== null
        ? this.getTvaIdFromByValeurApi(stationId, parsedFile)
        : of(null as number | null);

    return lookupFromByValeur$.pipe(
      switchMap((idFromApi) => {
        const idNum = idFromApi != null ? Number(idFromApi) : NaN;
        if (Number.isFinite(idNum) && idNum > 0) {
          return of(idNum);
        }
        const byListe = cache.find((t) => this.tvaListeEntryMatchesCell(t, parsedFile, trimmed));
        if (byListe?.id != null) {
          return of(Number(byListe.id));
        }
        const valeur = parsedFile !== null ? parsedFile : 0;
        const body: Tva = {
          client_id: clientId,
          station_id: stationId,
          valeur,
          type: '+',
          restriction: 'Pourcentage',
          statut: true
        };
        return this.tvaService.createTva(body, stationId).pipe(
          map((res) => {
            const t = res?.tva;
            if (t?.id != null) {
              cache.push(t);
              return Number(t.id);
            }
            throw new Error('Réponse TVA invalide');
          })
        );
      })
    );
  }

  private getTvaIdFromByValeurApi(stationId: number, valeurNumeric: number): Observable<number | null> {
    return this.tvaService.getTvaByValeur(stationId, valeurNumeric).pipe(
      map((res) => this.extractTvaIdFromByValeurResponse(res)),
      catchError(() => of(null))
    );
  }

  private tvaListeEntryMatchesCell(t: Tva, parsedFile: number | null, rawTrimmed: string): boolean {
    const apiNum = Number(t.valeur);
    if (parsedFile !== null && !Number.isNaN(apiNum)) {
      return Math.abs(apiNum - parsedFile) < 1e-4;
    }
    const norm = (v: unknown) =>
      String(v ?? '')
        .trim()
        .replace(/%/g, '')
        .replace(/\s/g, '')
        .replace(',', '.');
    return norm(t.valeur) === norm(rawTrimmed);
  }

  private extractTvaIdFromByValeurResponse(res: unknown): number | null {
    if (res == null || typeof res === 'string') {
      return null;
    }
    const toId = (v: unknown): number | null => {
      if (v == null || v === '') {
        return null;
      }
      const n = Number(v);
      return Number.isFinite(n) && n > 0 ? n : null;
    };
    const envelope =
      (res as { data?: unknown })?.data != null &&
      typeof (res as { data?: unknown }).data === 'object' &&
      !Array.isArray((res as { data?: unknown }).data)
        ? (res as { data: unknown }).data
        : res;
    const env = envelope as {
      success?: boolean;
      tvaListe?: Array<{ id?: number }>;
      tva?: { id?: number };
      id?: number;
      tva_id?: number;
    };
    if (env?.success === true && Array.isArray(env.tvaListe) && env.tvaListe.length > 0) {
      const rid = toId(env.tvaListe[0]?.id);
      if (rid != null) {
        return rid;
      }
    }
    return toId(env?.id) ?? toId(env?.tva?.id) ?? toId(env?.tva_id);
  }

  private resolveOrCreateTaxeId(
    raw: string,
    cache: Taxe[],
    stationId: number,
    clientId: number
  ): Observable<number> {
    const t = raw.trim();
    const found = cache.find(
      (x) =>
        (x.nom && this.importLabelEquals(x.nom, t)) ||
        (x.label && this.importLabelEquals(x.label, t))
    );
    if (found?.id) {
      return of(found.id);
    }
    const newTaxe: Taxe = {
      id: 0,
      client_id: clientId,
      station_id: stationId,
      nom: t,
      label: t,
      valeur: 0,
      valeur_fixe: false,
      valeur_pourcentage: true,
      applique_avant_tva: false,
      applique_apres_tva: true,
      taxe_applique_sur_produits: true,
      type: '+',
      applique_sur_product: true,
      applique_sur_tous_document: false,
      statut: true
    };
    return this.taxeService.createTaxe(newTaxe, stationId).pipe(
      map((res) => {
        const tx = res?.taxe;
        if (tx?.id) {
          cache.push(tx);
          return tx.id;
        }
        throw new Error('Réponse taxe invalide');
      })
    );
  }

  private resolveOrCreateCategorieId(
    raw: string,
    cache: Categorie[],
    stationId: number,
    clientId: number
  ): Observable<number> {
    const lib = raw.trim();
    const found = cache.find((c) => c.libelle && this.importLabelEquals(c.libelle, lib));
    if (found?.id) {
      return of(found.id);
    }
    const cat: Categorie = {
      id: 0,
      user_id: clientId,
      station_id: stationId,
      libelle: lib
    };
    return this.categorieService.addCategorie(cat).pipe(
      map((created) => {
        cache.push(created);
        return created.id;
      })
    );
  }

  private resolveOrCreateMarqueId(
    raw: string,
    cache: Marque[],
    stationId: number,
    clientId: number
  ): Observable<number> {
    const lib = raw.trim();
    const found = cache.find((m) => m.libelle && this.importLabelEquals(m.libelle, lib));
    if (found?.id) {
      return of(found.id);
    }
    const marque: Marque = {
      id: 0,
      user_id: clientId,
      station_id: stationId,
      libelle: lib
    };
    return this.marqueService.addMarque(marque).pipe(
      map((created) => {
        cache.push(created);
        return created.id;
      })
    );
  }

  private resolveOrCreateUniteId(
    raw: string,
    cache: Unite[],
    stationId: number,
    clientId: number
  ): Observable<number> {
    const lib = raw.trim();
    const found = cache.find((u) => u.libelle && this.importLabelEquals(u.libelle, lib));
    if (found?.id) {
      return of(found.id);
    }
    const unite: Unite = {
      id: 0,
      user_id: clientId,
      station_id: stationId,
      libelle: lib
    };
    return this.uniteService.addUnite(unite).pipe(
      map((created) => {
        cache.push(created);
        return created.id;
      })
    );
  }

  private extractBulkImportErrorMessage(err: unknown): string {
    const e = err as {
      error?: { errors?: Array<{ message?: string } | string>; message?: string };
      message?: string;
    };
    if (e?.error?.errors && Array.isArray(e.error.errors)) {
      return e.error.errors.map((x) => (typeof x === 'string' ? x : x.message || String(x))).join(', ');
    }
    if (e?.error?.message) {
      return String(e.error.message);
    }
    return e?.message ? String(e.message) : "Erreur lors de l'import";
  }

  private isLikelyUniquenessConflict(err: unknown): boolean {
    const e = err as { status?: number };
    const status = e?.status;
    if (status === 409 || status === 422) {
      return true;
    }
    const msg = this.extractBulkImportErrorMessage(err).toLowerCase();
    return (
      msg.includes('unicité') ||
      msg.includes('unicite') ||
      msg.includes('duplicate') ||
      (msg.includes('conflit') && (msg.includes('unicité') || msg.includes('unicite') || msg.includes('dupliq')))
    );
  }
}
